import { useState, type ReactNode } from 'react';

import { parseDaysOrNull, ProceduralEventForm, toDateOrNull } from './procedural-event-form';
import type { ProceduralTimelineEventSuggestion } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { Icon } from '@/new/components/icon';
import { SearchableCombobox } from '@/new/components/searchable-combobox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/new/components/shadcn/accordion';
import { Button } from '@/new/components/shadcn/button';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { instantToCivilUtc } from '@/new/components/timeline-calculator/civil-date';
import { CountingReference } from '@/new/components/timeline-calculator/counting-reference';
import { EmbeddedCalculator } from '@/new/components/timeline-calculator/embedded-calculator';
import { cn } from '@/new/lib/utils';
import { COUNTING_OPTIONS, CountingKey } from '@/new/stores/timeline-calculator-store';

// Only Add and Update suggestions are ever generated (deletes never occur).
export type ReviewableKind = Exclude<ProceduralTimelineEventSuggestion['kind'], 'delete'>;

// Fields that define a calculated linked date; changing any warrants review.
export const LINKED_DATE_FIELDS: string[] = ['anchorEventId', 'deltaInterval', 'deltaIntervalType'];

export const KIND_META: Record<
  ReviewableKind,
  { label: string; icon: string; card: string; accent: string; hint: string }
> = {
  add: {
    label: 'Suggested event',
    icon: 'lucide--plus',
    card: 'border-sky-200 bg-sky-50/50',
    accent: 'text-sky-500',
    hint: 'This is a new event extracted from the uploaded procedural timeline. Verify all details are correct before accepting it.',
  },
  update: {
    label: 'Suggested update',
    icon: 'lucide--refresh-cw',
    card: 'border-violet-300 bg-violet-50',
    accent: 'text-violet-700',
    hint: 'This is an update to an existing event in the timeline. Verify the updated fields are correct before accepting it.',
  },
};

/** Small purple flag marking a field an Update suggestion changes. */
export function UpdatedChip() {
  return (
    <span className="rounded border border-violet-200 bg-violet-50 px-1.5 py-px text-[10px] font-medium text-violet-700">
      Updated
    </span>
  );
}

/** Bordered accordion: an icon + title trigger over collapsible content. */
function CollapsibleSection({
  icon,
  title,
  titleAdornment,
  open,
  onOpenChange,
  className,
  children,
}: {
  icon: string;
  title: string;
  titleAdornment?: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('rounded-lg border border-gray-200', className)}>
      <Button
        type="button"
        variant="ghost"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
        className="flex h-auto w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-transparent"
      >
        <span className="flex items-center gap-1.5">
          <Icon className={cn('text-teal-500', icon)} />
          {title}
          {titleAdornment}
        </span>
        <Icon className={cn('lucide--chevron-down text-gray-400 transition-transform', open && 'rotate-180')} />
      </Button>
      {open && children}
    </div>
  );
}

interface LinkedDateCalculatorProps {
  form: ProceduralEventForm;
  eventsById: Record<string, ProceduralTimelineEvent>;
  matterId: string;
  /** The suggestion's existing date, pre-selected until inputs change. */
  initialSelectedDate: Date | null;
  onCalculationConfirmed: () => void;
  /** Fields this Update suggestion changes, for the per-field "Updated" flags. */
  changedSet: Set<string>;
  /** Expand the calculator on mount (a linked date requiring verification). */
  defaultOpen: boolean;
}

/**
 * Always-visible auto-calculating calculator for a linked (anchored) date.
 * "Use X" writes the result to the start-date field above.
 */
export function LinkedDateCalculator({
  form,
  eventsById,
  matterId,
  initialSelectedDate,
  onCalculationConfirmed,
  changedSet,
  defaultOpen,
}: LinkedDateCalculatorProps) {
  const anchorEvent = form.values.anchorEventId !== '' ? (eventsById[form.values.anchorEventId] ?? null) : null;
  const [openItem, setOpenItem] = useState(defaultOpen ? 'calculate-linked-date' : '');

  return (
    <Accordion type="single" collapsible value={openItem} onValueChange={setOpenItem}>
      <AccordionItem value="calculate-linked-date" className="rounded-lg bg-gray-50">
        <AccordionTrigger className="rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-gray-700">
          <span className="flex items-center gap-2">
            <Icon className="lucide--calculator text-teal-500" />
            Calculate linked date
            <Tooltip>
              <TooltipTrigger asChild>
                <span role="button" tabIndex={0} className="flex" onClick={(e) => e.stopPropagation()}>
                  <Icon className="lucide--info h-3 w-3 text-gray-400 hover:text-gray-700" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" align="start" className="max-w-xs">
                This suggested event is dependent upon or linked to another event in the timeline, use the calculator to
                verify the date of the suggested event.
              </TooltipContent>
            </Tooltip>
          </span>
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-3 rounded-b-lg border-t-0 border-gray-200 bg-gray-50 p-5 py-4">
          <div className="flex flex-col gap-3">
            <div>
              <div className="mb-2 flex items-center gap-1.5">
                <Label className="mb-0">Depends upon</Label>
                {changedSet.has('anchorEventId') && <UpdatedChip />}
              </div>
              <SearchableCombobox
                options={form.anchorOptions}
                value={form.values.anchorEventId}
                onChange={form.setAnchorEventId}
                placeholder="None"
                searchPlaceholder="Search events"
                emptyText="No matching events."
                triggerClassName="w-full justify-between font-normal bg-white"
                containerClassName="w-full [&>span]:block"
              />
            </div>
            <div>
              <div className="mb-2 flex items-center gap-1.5">
                <Label htmlFor="review-delta-days" className="mb-0">
                  Days after
                </Label>
                {changedSet.has('deltaInterval') && <UpdatedChip />}
              </div>
              <Input
                id="review-delta-days"
                type="number"
                value={form.values.deltaDays}
                onChange={(e) => form.setDeltaDays(e.target.value)}
                onWheel={(e) => e.currentTarget.blur()}
                disabled={form.values.anchorEventId === ''}
                placeholder="e.g. 14"
              />
            </div>
            <div>
              <div className="mb-2 flex items-center gap-1.5">
                <Label className="mb-0">Counting method</Label>
                <CountingReference side="top" align="end" />
                {changedSet.has('deltaIntervalType') && <UpdatedChip />}
              </div>
              <Select
                value={form.values.countingKey}
                onValueChange={(v) => form.setCountingKey(v as CountingKey)}
                disabled={form.values.anchorEventId === ''}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTING_OPTIONS.map((option) => (
                    <SelectItem key={option.key} value={option.key}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <EmbeddedCalculator
            matterId={matterId}
            sourceDate={toDateOrNull(anchorEvent?.startDateTime)}
            days={parseDaysOrNull(form.values.deltaDays) ?? 0}
            counting={form.values.countingKey}
            initialSelectedDate={initialSelectedDate}
            onApply={(date) => {
              form.setStartDate(instantToCivilUtc(date));
              onCalculationConfirmed();
              setOpenItem('');
            }}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

/** Collapsed accordion surfacing the AI's reasoning, tucked at the card's foot. */
export function ExtractedReasoningSection({ reasoning }: { reasoning: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <CollapsibleSection
      className="mt-4"
      icon="lucide--sparkles"
      title="Why was this suggested?"
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <p className="border-t border-gray-200 px-3 py-2 text-sm text-gray-600">{reasoning}</p>
    </CollapsibleSection>
  );
}
